import moment from "moment";
import { FC, useCallback, useState } from "react";
import { FaCalendarDays, FaMoneyBill1 } from "react-icons/fa6";
import { HiOutlineEye, HiOutlinePencil } from "react-icons/hi2";
import { MdOutlineQueryStats } from "react-icons/md";
import { PiCaretLeftBold, PiCaretRightBold } from "react-icons/pi";
import { RiCalendar2Line, RiCarLine, RiMoneyDollarBoxLine, RiToolsFill } from "react-icons/ri";
import ReactPaginate from "react-paginate";
import { useParams } from "react-router-dom";
import { ActionIcon, Button, Select, SelectOption, Text, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { FormDrawerView } from "../../../../../components/forms/FormDrawer";
import MaintenanceDetailModal from "../../../../../components/forms/MaintenanceDetailModal";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../../../components/table/components/TableFilter";
import useFetch from "../../../../../hooks/useFetch";
import { MaintenanceService } from "../../../../../services/maintenance/maintenanceService";
import { Maintenance, MaintenancesApiResponse, MaintenanceStatisticsApiResponse } from "../../../../../type/entities/maintenance-type";
import { maintenanceTypes } from "../../../../../utils/SelectorMapping";
import MaintenanceForm from "../../../../maintenances/components/MaintenanceForm";
import VehicleMaintenanceFilters from "./VehicleMaintenanceFilters";

// Usando el tipo Maintenance de la API

const VehicleMaintenance: FC = () => {
    const { id } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMaintenance, setSelectedMaintenance] = useState<Maintenance | null>(null);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [maintenanceForDetail, setMaintenanceForDetail] = useState<Maintenance | null>(null);

    const service = new MaintenanceService();

    // HOOKS
    const { filters, updateFilters, resetFilters, updatePageSize, updatePage, updateFilterOrder } = useFiltersPR();

    // Fetch maintenance data for this vehicle
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.listMaintenances(filters);
        return response.getResponseData() as MaintenancesApiResponse;
    }, [filters]));

    // Fetch maintenance statistics for this vehicle
    const [statisticsData, statisticsLoading, statisticsError, refetchStatistics] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.getMaintenanceStatistics(filters);
        return response.getResponseData() as MaintenanceStatisticsApiResponse;
    }, [filters]));


    const handleAddMaintenance = useCallback(() => {
        setSelectedMaintenance(null);
        setIsModalOpen(true);
    }, []);

    const handleEditMaintenance = useCallback((maintenance: Maintenance) => {
        setSelectedMaintenance(maintenance);
        setIsModalOpen(true);
    }, []);

    const handleDelete = useCallback(async (maintenanceId: string) => {
        try {
            await service.deleteMaintenance(maintenanceId);
            toast.success('Mantenimiento eliminado correctamente');
            refetch(); // Refresh maintenance data
            // Note: Statistics will be automatically refreshed due to dependency on id
        } catch (error) {
            console.error('Error deleting maintenance:', error);
            toast.error('Error al eliminar el mantenimiento');
        }
    }, [service, refetch]);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const maintenanceHistory = data?.data || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Mantenimientos del Vehículo</h2>
                    <p className="text-gray-600 mt-1">Gestiona el historial de mantenimientos y servicios</p>
                </div>
                {userCan('create', 'vehicle') && (
                    <Button
                        onClick={() => {
                            setIsModalOpen(true);
                            setSelectedMaintenance(null);
                        }}
                        className="bg-primary text-white px-6 py-2.5 font-medium"
                        size="lg"
                    >
                        Nuevo Mantenimiento
                    </Button>
                )}
            </div>


            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-blue-700 mb-1">Total Mantenimientos</h3>
                            <p className="text-3xl font-bold text-blue-900">
                                {statisticsData?.totalMaintenances || maintenanceHistory.length}
                            </p>
                        </div>
                        <div className="w-[4rem] h-[4rem] bg-blue-600 rounded-lg flex items-center justify-center">
                            <span className="text-white text-3xl font-bold"><RiToolsFill /></span>
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-green-700 mb-1">Costo Total</h3>
                            <p className="text-3xl font-bold text-green-900">
                                {formatCurrency(statisticsData?.totalCost || 0)}
                            </p>
                        </div>
                        <div className="w-[4rem] h-[4rem] bg-green-500 rounded-lg flex items-center justify-center">
                            <span className="text-white text-3xl font-bold"><FaMoneyBill1 /></span>
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-purple-700 mb-1">Costo Promedio</h3>
                            <p className="text-3xl font-bold text-purple-900">
                                {formatCurrency(statisticsData?.averageCost || 0)}
                            </p>
                        </div>
                        <div className="w-[4rem] h-[4rem] bg-purple-500 rounded-lg flex items-center justify-center">
                            <span className="text-white text-3xl font-bold"><MdOutlineQueryStats /></span>
                        </div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-orange-700 mb-1">Último Mantenimiento</h3>
                            <p className="text-lg font-bold text-orange-900">
                                {maintenanceHistory.length > 0
                                    ? moment(maintenanceHistory[0]?.date.date).format('DD/MM/YYYY')
                                    : 'N/A'
                                }
                            </p>
                        </div>
                        <div className="w-[4rem] h-[4rem] bg-orange-400 rounded-lg flex items-center justify-center">
                            <span className="text-white text-3xl font-bold"><FaCalendarDays /></span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Maintenance History */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <h3 className="text-xl font-semibold text-gray-900">Historial de Mantenimientos</h3>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">
                                {data?.totalRegisters || 0} {(data?.totalRegisters || 0) === 1 ? 'registro' : 'registros'}
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">Mostrar:</span>
                                <Select
                                    options={[
                                        { value: 5, label: '5' },
                                        { value: 10, label: '10' },
                                        { value: 25, label: '25' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' }
                                    ]}
                                    className="w-[70px] bg-white"
                                    value={filters.limit}
                                    onChange={(v: SelectOption) => updatePageSize(v)}
                                    selectClassName="font-semibold text-sm ring-0 shadow-sm h-9"
                                    dropdownClassName="font-medium [&_li]:justify-center [&_li]:text-sm"
                                />
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setOpenFilters(!openFilters)}
                                className="h-9 pe-3 ps-3"
                            >
                                Filtros
                            </Button>
                        </div>
                    </div>
                </div>



                {maintenanceHistory.length === 0 ? (
                    <div className="p-8">
                        <div className="text-center py-16">
                            <div className="mx-auto w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                                <span className="text-5xl text-gray-400">🔧</span>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">No hay mantenimientos registrados</h3>
                            <p className="text-gray-500 mb-8 max-w-md mx-auto">Comienza registrando el primer mantenimiento de este vehículo para llevar un control completo de su historial</p>
                            {userCan('create', 'vehicle') && (
                                <Button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 font-medium"
                                    size="lg"
                                >
                                    Registrar Primer Mantenimiento
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="p-6 space-y-6">
                        {maintenanceHistory.map((record: Maintenance) => (
                            <div
                                key={record.id}
                                className="bg-gradient-to-r from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-200"
                            >
                                <div className="flex flex-col lg:flex-row justify-between gap-6">
                                    <div className="flex-1">
                                        {/* Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                                    <span className="text-white text-3xl font-bold"><RiToolsFill /></span>
                                                </div>
                                                <div>
                                                    <h4 className="text-xl font-semibold text-gray-900">
                                                        {/* Search in maintenanceTypes de label of record.type */}
                                                        {maintenanceTypes.find(type => type.value === record.type)?.label || record.type}
                                                    </h4>
                                                </div>
                                            </div>

                                            <div className="flex gap-2">
                                                <Tooltip content="Ver detalles">
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:bg-blue-50"
                                                        onClick={() => {
                                                            setMaintenanceForDetail(record);
                                                            setDetailModalOpen(true);
                                                        }}
                                                    >
                                                        <HiOutlineEye className="h-4 w-4" />
                                                    </ActionIcon>
                                                </Tooltip>
                                                {userCan('edit', 'maintenance') && (
                                                    <Tooltip content="Editar">
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="outline"
                                                            className="hover:bg-yellow-50"
                                                            onClick={() => handleEditMaintenance(record)}
                                                        >
                                                            <HiOutlinePencil className="h-4 w-4" />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                                {userCan('delete', 'maintenance') && (
                                                    <DeletePopover
                                                        title="¿Eliminar mantenimiento?"
                                                        description="Esta acción no se puede deshacer."
                                                        onDelete={() => handleDelete(record.id)}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Description */}
                                        {record.description && (
                                            <div className="mb-6">
                                                <p className="text-gray-700 leading-relaxed bg-gray-50 p-2 rounded-lg">
                                                    {record.description}
                                                </p>
                                            </div>
                                        )}

                                        {/* Details Grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-blue-500"><RiCalendar2Line /></span>
                                                    <span className="text-sm font-medium text-gray-500">Fecha</span>
                                                </div>
                                                <p className="text-lg font-semibold text-gray-900">
                                                    {moment(record.date.date).format('DD/MM/YYYY')}
                                                </p>
                                            </div>
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-purple-500"><RiCarLine /></span>
                                                    <span className="text-sm font-medium text-gray-500">Vehículo</span>
                                                </div>
                                                <p className="text-lg font-semibold text-gray-900">
                                                    {record.vehicle?.plateNumber || 'N/A'}
                                                </p>
                                            </div>
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-green-500"><RiMoneyDollarBoxLine /></span>
                                                    <span className="text-sm font-medium text-gray-500">Costo</span>
                                                </div>
                                                <p className="text-lg font-semibold text-green-600">
                                                    {formatCurrency(record.cost || 0)}
                                                </p>
                                            </div>
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-amber-500"><RiCarLine /></span>
                                                    <span className="text-sm font-medium text-gray-500">Km</span>
                                                </div>
                                                <p className="text-lg font-semibold text-gray-900">
                                                    {record.odometerKm != null
                                                        ? `${Number(record.odometerKm).toLocaleString('es-ES')} km`
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-orange-500"><RiCalendar2Line /></span>
                                                    <span className="text-sm font-medium text-gray-500">Creado</span>
                                                </div>
                                                <p className="text-lg font-semibold text-gray-900">
                                                    {moment(record.createdAt.date).format('DD/MM/YYYY')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {data && data.totalRegisters > 0 && (
                    <div className="flex w-full items-center justify-between mt-6 p-6">

                        <div className="flex w-full items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <Text className="hidden font-medium text-gray-900 md:block">
                                    Filas por página:
                                </Text>
                                <Select
                                    options={[
                                        { value: 5, label: '5' },
                                        { value: 10, label: '10' },
                                        { value: 25, label: '25' },
                                        { value: 50, label: '50' },
                                        { value: 100, label: '100' }
                                    ]}
                                    className="w-[70px] bg-white"
                                    value={filters.limit}
                                    onChange={(v: SelectOption) => updatePageSize(v)}
                                    selectClassName="font-semibold text-sm ring-0 shadow-sm h-9"
                                    dropdownClassName="font-medium [&_li]:justify-center [&_li]:text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-2">
                                <ReactPaginate
                                    pageCount={Math.ceil((data.totalRegisters || 0) / filters.limit)}
                                    pageRangeDisplayed={2}
                                    initialPage={filters.page <= 1 ? 0 : filters.page - 1}
                                    onPageChange={(page) => updatePage(page)}
                                    nextLabel={<PiCaretRightBold />}
                                    previousLabel={<PiCaretLeftBold />}
                                    className="flex items-center justify-center space-x-1 text-gray-700"
                                    pageClassName="inline-flex"
                                    pageLinkClassName="inline-flex items-center px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                    activeClassName=""
                                    activeLinkClassName="pagination-active"
                                    previousClassName="inline-flex"
                                    previousLinkClassName="inline-flex items-center px-1.5 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                    nextClassName="inline-flex"
                                    nextLinkClassName="inline-flex items-center px-1.5 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                    breakClassName="inline-flex"
                                    breakLinkClassName="inline-flex items-center px-1.5 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary rounded-md"
                                />
                            </div>
                        </div>
                    </div>
                )}

            </div>


            {/* Filter Drawer */}
            <FilterDrawerView
                isOpen={openFilters}
                setOpenDrawer={setOpenFilters}
                drawerTitle="Filtros de Mantenimientos"
                onClear={() => resetFilters(50)}
            >
                <VehicleMaintenanceFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                />
            </FilterDrawerView>

            {/* Detail Modal */}
            <MaintenanceDetailModal
                isOpen={detailModalOpen}
                onClose={() => {
                    setDetailModalOpen(false);
                    setMaintenanceForDetail(null);
                }}
                maintenance={maintenanceForDetail}
            />

            {/* Form Drawer */}
            <FormDrawerView
                isOpen={isModalOpen}
                setOpenDrawer={setIsModalOpen}
                drawerTitle={selectedMaintenance ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}
            >
                <MaintenanceForm
                    maintenance={selectedMaintenance}
                    setOpenModal={setIsModalOpen}
                    refetch={refetch}
                    vehicleId={id}
                    refetchStatistics={refetchStatistics}
                />
            </FormDrawerView>
        </div>
    );
};

export default VehicleMaintenance;